import { useEffect, useRef, useState, useCallback } from 'react';
import axiosInstance from '../../../axiosConfig';

const OVERVIEW_POLL_MS = 15_000;

export function useInfraHealthOverview() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);

    const fetchOnce = useCallback(async () => {
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            const res = await axiosInstance.get('/api/infra-health/overview', { signal: controller.signal });
            setData(res.data);
            setError(null);
        } catch (err) {
            if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOnce();
        const iv = setInterval(() => {
            if (!document.hidden) fetchOnce();
        }, OVERVIEW_POLL_MS);
        const onVis = () => { if (!document.hidden) fetchOnce(); };
        document.addEventListener('visibilitychange', onVis);
        return () => {
            clearInterval(iv);
            document.removeEventListener('visibilitychange', onVis);
            if (abortRef.current) abortRef.current.abort();
        };
    }, [fetchOnce]);

    return { data, loading, error, refresh: fetchOnce };
}

export function useHostDetails(hostName, windowSec = 3600) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchOnce = useCallback(async () => {
        if (!hostName) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/infra-health/hosts/${encodeURIComponent(hostName)}/details`, {
                params: { window: windowSec, graphs: 40 },
            });
            setData(res.data);
            setError(null);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [hostName, windowSec]);

    useEffect(() => {
        setData(null);
        if (hostName) fetchOnce();
    }, [hostName, windowSec, fetchOnce]);

    return { data, loading, error, refresh: fetchOnce };
}

export function useRoomSensorHistory(windowSec = 1800) {
    const [series, setSeries] = useState({ temperature: [], humidity: [] });
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            try {
                const res = await axiosInstance.get('/api/infra-health/sensors/room/history', { params: { window: windowSec } });
                if (!cancelled) setSeries({ temperature: res.data.temperature || [], humidity: res.data.humidity || [] });
            } catch (_) { /* ignore */ }
            finally { if (!cancelled) setLoading(false); }
        };
        run();
        const iv = setInterval(run, 60_000);
        return () => { cancelled = true; clearInterval(iv); };
    }, [windowSec]);
    return { series, loading };
}
